/**
 * "Live Event notifications are here." The one-time set-up of the automatic
 * event countdowns (2026-10-30 owner ask).
 *
 * Shows once per account: a picture of the countdown on THIS kind of phone
 * (the Lock Screen card and Dynamic Island on iPhone, the ongoing
 * notification on Android), a "Show me on my phone" that puts the real card
 * up, then the choice: card or notification, how long before, and the event
 * types, all the time-boxed ones ticked plus whatever the trainer has tapped
 * "Remind me" on before. One tap turns it on for every such event from now
 * on; the Calendar's Remind me buttons on those events become "Countdown set".
 *
 * Its turn in the first-run queue is lib/firstRunPrompts ("live-events",
 * first in line from the second session). An account that already holds a
 * choice, made on another phone, is marked done without seeing it. Settings
 * can reopen it by hand (openLiveEventsSetup).
 */
import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { BellRing, X } from "lucide-react";
import { useAuth } from "@/components/AuthProvider";
import { useSession } from "@/components/SessionProvider";
import { LiveEventCountdownMock } from "@/components/LiveEventCountdownMock";
import { LiveEventPrefsControls } from "@/components/LiveEventPrefsControls";
import { useToast } from "@/hooks/use-toast";
import { claimFirstRunPrompt, markFirstRunPromptDone } from "@/lib/firstRunPrompts";
import { sessionModalOpen, useFirstRunPromptDue } from "@/lib/useFirstRunPrompt";
import { previewLiveActivity, startLiveActivityListeners } from "@/lib/liveActivities";
import { armSubscribedEventsFromFeed } from "@/lib/liveEventArming";
import { fetchLiveEventPrefs, saveLiveEventPrefs, suggestedPrefs, type LiveEventPrefs } from "@/lib/liveEventPrefs";
import { fireLocalNotification, requestNotificationPermission } from "@/lib/notifications";
import { getPlatform } from "@/lib/platform";

/** Settings dispatches this to reopen the sheet by hand. */
export const LIVE_EVENTS_OPEN_EVENT = "nexus:openLiveEventsSetup";

export function openLiveEventsSetup(): void {
  if (typeof window !== "undefined") window.dispatchEvent(new Event(LIVE_EVENTS_OPEN_EVENT));
}

/** The first screen gets this long before the card lands on it. */
const SHOW_DELAY_MS = 8_000;

export function LiveEventsIntroSheet(): JSX.Element | null {
  const { account } = useAuth();
  const session = useSession();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const platform = getPlatform();
  const due = useFirstRunPromptDue("live-events");
  const [visible, setVisible] = useState(false);
  const [manual, setManual] = useState(false);
  const [draft, setDraft] = useState<LiveEventPrefs>(() => suggestedPrefs());
  const [busy, setBusy] = useState(false);
  const loadedFor = useRef<number | null>(null);

  // Its turn: ask the account first. A choice already on file (another
  // phone) is the answer; otherwise the sheet opens with the defaults plus
  // the types the trainer has reminded before, after the first screen has
  // had its moment.
  const accountId = account?.id ?? null;
  useEffect(() => {
    if (!due || accountId == null) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    void fetchLiveEventPrefs().then(({ prefs, remindedTypes }) => {
      if (cancelled) return;
      if (prefs) {
        markFirstRunPromptDone("live-events");
        return;
      }
      loadedFor.current = accountId;
      setDraft(suggestedPrefs(remindedTypes));
      timer = setTimeout(() => {
        claimFirstRunPrompt("live-events");
        setVisible(true);
      }, SHOW_DELAY_MS);
    });
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [due, accountId]);

  // Opened by hand from Settings: the current choice, no delay, no yields.
  useEffect(() => {
    const open = () => {
      void fetchLiveEventPrefs().then(({ prefs, remindedTypes }) => {
        setDraft(prefs ? { ...prefs, enabled: true } : suggestedPrefs(remindedTypes));
        setManual(true);
        setVisible(true);
      });
    };
    window.addEventListener(LIVE_EVENTS_OPEN_EVENT, open);
    return () => window.removeEventListener(LIVE_EVENTS_OPEN_EVENT, open);
  }, []);

  if (!visible) return null;
  if (!manual && sessionModalOpen(session)) return null;

  const native = platform === "ios" || platform === "android";
  const count = draft.types.length;

  const close = () => {
    setVisible(false);
    setManual(false);
  };

  const notNow = async () => {
    close();
    markFirstRunPromptDone("live-events");
    // "Not now" is a choice too: off, remembered on the account, so no other
    // phone asks again. Settings is the way back.
    await saveLiveEventPrefs({ ...draft, enabled: false });
  };

  const turnOn = async () => {
    if (busy || count === 0) return;
    setBusy(true);
    try {
      let permission: string = "granted";
      // The Android card and every plain notification need the OS permission;
      // the iPhone card needs Live Activities on, which the preview explains.
      if (native && (platform === "android" || draft.delivery === "push")) {
        permission = await requestNotificationPermission({ reprompt: true }).catch(() => "unknown");
      }
      if (platform === "ios" && draft.delivery === "live") void startLiveActivityListeners();
      const saved = await saveLiveEventPrefs({ ...draft, enabled: true });
      markFirstRunPromptDone("live-events");
      close();
      const outcome = await armSubscribedEventsFromFeed(queryClient);
      if (permission !== "granted" && native) {
        toast({
          title: "Live events on, notifications off for Nexus Dex.",
          description: "Turn them on for Nexus Dex in your phone's Settings and the countdowns will arrive.",
        });
      } else {
        toast({
          title: `Live events on for ${count} event ${count === 1 ? "type" : "types"}.`,
          description: !native
            ? "Countdowns arrive on the Nexus Dex app on your phone. Change this any time in Settings."
            : outcome.armed > 0
              ? `${outcome.armed} upcoming ${outcome.armed === 1 ? "event is" : "events are"} set on this phone. Change this any time in Settings.`
              : "Every matching event from now on is set. Change this any time in Settings.",
        });
      }
      if (!saved) {
        toast({ title: "Saved on this phone only.", description: "The account copy did not save; it will on the next launch." });
      }
    } finally {
      setBusy(false);
    }
  };

  const showMe = async () => {
    if (!native) {
      toast({ title: "The countdown is a phone thing.", description: "Open Nexus Dex on your phone to see it live." });
      return;
    }
    if (draft.delivery === "live") {
      const r = await previewLiveActivity(Date.now(), draft.leadMinutes * 60_000);
      toast({ title: r.ok ? "Look at your Lock Screen." : "No countdown on this phone.", description: r.detail });
      return;
    }
    const perm = await requestNotificationPermission({ reprompt: true }).catch(() => "unknown");
    if (perm !== "granted") {
      toast({ title: "Notifications are off for Nexus Dex.", description: "Turn them on for Nexus Dex in your phone's Settings." });
      return;
    }
    const r = await fireLocalNotification({
      id: 990_001,
      title: draft.leadMinutes === 0 ? "Pikachu Spotlight Hour is live" : `Pikachu Spotlight Hour starts in ${draft.leadMinutes === 60 ? "1 hour" : `${draft.leadMinutes} minutes`}`,
      body: "2× Catch Stardust. Open Pokémon GO.",
    });
    toast({ title: r.ok ? "Sent." : "Could not send the example.", description: r.ok ? "That is the notification you would get." : r.detail });
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Live event notifications"
      data-testid="live-events-intro"
      className="fixed inset-0 z-[70] flex items-end md:items-center justify-center px-3 py-3 bg-black/75 backdrop-blur-sm anim-fade-up"
      onClick={manual ? close : undefined}
    >
      <div
        className="relative w-full max-w-md v2-neon-card anim-fade-scale max-h-[90dvh] overflow-y-auto overscroll-contain p-4"
        onClick={(e) => e.stopPropagation()}
        style={{
          boxShadow: "0 30px 80px hsl(0 0% 0% / 0.85), 0 0 0 1px var(--accent-fill-glow), 0 0 40px var(--accent-fill-glow-subtle)",
          WebkitOverflowScrolling: "touch",
          touchAction: "pan-y",
        }}
      >
        <button
          type="button"
          onClick={() => void (manual ? close() : notNow())}
          aria-label="Not now"
          data-testid="live-events-intro-close"
          className="absolute right-3 top-3 w-8 h-8 rounded-full grid place-items-center"
          style={{ background: "var(--surface-raised)", border: "1px solid var(--border-subtle)", color: "var(--text-secondary)" }}
        >
          <X className="w-4 h-4" />
        </button>

        <span
          className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.16em]"
          style={{ background: "var(--accent-fill-glow-faint)", border: "1px solid var(--accent-fill-glow)", color: "var(--accent-fill)" }}
        >
          <BellRing className="w-3 h-3" /> Live events
        </span>
        <h2 className="mt-2 text-[22px] font-black leading-tight pr-9" style={{ color: "var(--text-primary)" }}>
          Event countdowns. <span className="text-gradient-chrome-green">Automatic.</span>
        </h2>
        <p className="mt-1 text-[12px] leading-snug" style={{ color: "var(--text-secondary)" }}>
          {platform === "ios"
            ? "A live countdown on your Lock Screen and in the Dynamic Island before every event you care about, then the event while it is on. No more tapping Remind me."
            : platform === "android"
              ? "A live countdown in your notifications before every event you care about, then the event while it is on. No more tapping Remind me."
              : "A live countdown on your phone before every event you care about, then the event while it is on. Set it up here; it arrives on the Nexus Dex app."}
        </p>

        <div className="mt-3">
          <LiveEventCountdownMock platform={platform} lead={draft.leadMinutes} delivery={draft.delivery} />
          <button
            type="button"
            onClick={() => void showMe()}
            data-testid="live-events-intro-preview"
            className="mt-2 text-[11px] font-bold"
            style={{ color: "var(--accent-fill)" }}
          >
            Show me on my phone
          </button>
        </div>

        <div className="mt-4">
          <LiveEventPrefsControls value={draft} onChange={setDraft} showDelivery={native} testId="live-events-intro" />
        </div>

        <button
          type="button"
          onClick={() => void turnOn()}
          disabled={busy || count === 0}
          data-testid="live-events-intro-turn-on"
          className="v2-neon-cta mt-5 w-full py-3 text-sm font-black inline-flex items-center justify-center gap-2 disabled:opacity-60"
        >
          <BellRing className="w-4 h-4" />
          {count === 0 ? "Pick at least one event type" : `Turn on for ${count} event ${count === 1 ? "type" : "types"}`}
        </button>
        {!manual && (
          <button
            type="button"
            onClick={() => void notNow()}
            data-testid="live-events-intro-not-now"
            className="mt-2 w-full py-2 text-[12px] font-semibold"
            style={{ color: "var(--text-secondary)" }}
          >
            Not now
          </button>
        )}
        <p className="mt-2 text-[10px] leading-snug text-center" style={{ color: "var(--text-ink-faint)" }}>
          One-place events (a GO Tour city) are only counted down when you mark yourself as going. Change any of this in Settings.
        </p>
      </div>
    </div>
  );
}
